import { z } from 'zod';
import { idSchema, isoDateSchema, paginated, paginationQuerySchema } from './common.js';
/**
 * Cobrança pelo gateway de pagamento.
 *
 * A PARCELA do contas a receber continua sendo a verdade do que se deve; a
 * cobrança é só o meio de pagar. Quem dá baixa é o webhook, e a baixa passa
 * pela mesma regra central do Financeiro que a baixa manual usa.
 */
export const PAYMENT_PROVIDERS = ['ASAAS'];
export const paymentProviderSchema = z.enum(PAYMENT_PROVIDERS);
export const PAYMENT_ENVIRONMENTS = ['SANDBOX', 'PRODUCAO'];
export const paymentEnvironmentSchema = z.enum(PAYMENT_ENVIRONMENTS);
export const PAYMENT_ENVIRONMENT_LABELS = {
    SANDBOX: 'Teste (sandbox)',
    PRODUCAO: 'Produção — cobra de verdade',
};
/* ============================== Configuração ============================= */
export const paymentGatewaySchema = z.object({
    id: idSchema,
    companyId: idSchema,
    provider: paymentProviderSchema,
    environment: paymentEnvironmentSchema,
    /// Só os últimos dígitos. A chave nunca volta inteira para a tela — quem a
    /// tem cobra em nome da empresa e mexe no dinheiro que entra.
    apiKeyHint: z.string(),
    bankAccountId: idSchema,
    bankAccountName: z.string(),
    isActive: z.boolean(),
    /// Emitir a cobrança da mensalidade do Clube sozinho, na geração.
    autoChargeMembership: z.boolean(),
    lastEventAt: z.string().nullable(),
    /// Endereço que o operador cola no painel do ASAAS.
    webhookUrl: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const paymentGatewayInputSchema = z.object({
    provider: paymentProviderSchema.default('ASAAS'),
    environment: paymentEnvironmentSchema,
    /// Em branco na edição = manter a chave que já está guardada.
    apiKey: z.string().trim().min(10, 'Cole a chave da API do ASAAS').optional(),
    bankAccountId: idSchema,
    isActive: z.boolean().default(false),
    /// Nasce desligada: passar a cobrar sozinho é decisão de quem opera.
    autoChargeMembership: z.boolean().default(false),
});
/** O segredo do webhook só aparece UMA vez, na hora em que é gerado. */
export const webhookTokenSchema = z.object({
    webhookUrl: z.string(),
    token: z.string(),
});
/* ================================ Cobrança =============================== */
export const CHARGE_BILLING_TYPES = ['PIX', 'BOLETO', 'CARTAO', 'ESCOLHA_DO_CLIENTE'];
export const chargeBillingTypeSchema = z.enum(CHARGE_BILLING_TYPES);
export const CHARGE_BILLING_TYPE_LABELS = {
    PIX: 'PIX',
    BOLETO: 'Boleto',
    CARTAO: 'Cartão',
    ESCOLHA_DO_CLIENTE: 'O cliente escolhe',
};
export const CHARGE_STATUSES = ['PENDENTE', 'RECEBIDA', 'VENCIDA', 'ESTORNADA', 'CANCELADA'];
export const chargeStatusSchema = z.enum(CHARGE_STATUSES);
export const CHARGE_STATUS_LABELS = {
    PENDENTE: 'Aguardando pagamento',
    RECEBIDA: 'Paga',
    VENCIDA: 'Vencida',
    ESTORNADA: 'Estornada',
    CANCELADA: 'Cancelada',
};
/** Status em que a cobrança ainda pode ser paga — só existe uma por parcela. */
export const LIVE_CHARGE_STATUSES = ['PENDENTE', 'VENCIDA'];
export const chargeSchema = z.object({
    id: idSchema,
    companyId: idSchema,
    installmentId: idSchema,
    /// Do lançamento que a parcela pertence, para a tela não precisar buscar.
    entryId: idSchema,
    entryCode: z.string(),
    entryDescription: z.string(),
    installmentNumber: z.number().int(),
    personName: z.string().nullable(),
    provider: paymentProviderSchema,
    externalId: z.string(),
    billingType: chargeBillingTypeSchema,
    status: chargeStatusSchema,
    valueCents: z.number().int(),
    dueDate: z.string(),
    invoiceUrl: z.string().nullable(),
    bankSlipUrl: z.string().nullable(),
    pixPayload: z.string().nullable(),
    paidAt: z.string().nullable(),
    /// A taxa do gateway. A baixa é pelo BRUTO: o associado pagou o valor
    /// cheio, e a taxa é despesa nossa, não desconto dele.
    feeCents: z.number().int(),
    netValueCents: z.number().int(),
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const createChargeSchema = z.object({
    installmentId: idSchema,
    billingType: chargeBillingTypeSchema.default('ESCOLHA_DO_CLIENTE'),
    /// Em branco usa o vencimento da própria parcela, que é o certo em 99% dos
    /// casos — o campo existe para renegociação combinada com o associado.
    dueDate: isoDateSchema.optional(),
    /// Texto que o cliente vê na fatura. Em branco usa a descrição do
    /// lançamento.
    description: z.string().trim().max(500).optional().nullable(),
});
export const cancelChargeSchema = z.object({
    reason: z.string().trim().min(5, 'Diga por que a cobrança está sendo cancelada').max(300),
});
export const chargeListQuerySchema = paginationQuerySchema.extend({
    status: chargeStatusSchema.optional(),
    personId: idSchema.optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
});
export const chargeListResponseSchema = paginated(chargeSchema);
/* ================================= Reais ================================= */
/**
 * O gateway fala em REAIS decimais; nós falamos em CENTAVOS inteiros.
 *
 * A conversão acontece na borda, uma vez, e é testada. Multiplicar por 100 em
 * ponto flutuante erra: `19.99 * 100` é 1998.9999999999998, e truncar isso dá
 * R$ 19,98 — um centavo a menos em toda cobrança terminada em 9.
 */
export function centsToReais(cents) {
    return Math.round(cents) / 100;
}
export function reaisToCents(value) {
    const numero = typeof value === 'string' ? Number(value) : value;
    if (!Number.isFinite(numero))
        return 0;
    // O arredondamento é sobre o valor JÁ em centavos, não sobre o produto em
    // ponto flutuante: é isso que salva o 19,99.
    return Math.round(numero * 100);
}
//# sourceMappingURL=cobranca.js.map